import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { 
  Box, 
  Button, 
  IconButton, 
  InputAdornment, 
  Paper, 
  TextField, 
  Typography, 
  useMediaQuery, 
  Alert, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem 
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../../utils/axiosInstance';
import { getUser } from '../../../services/authService';

const AgentUpdate = () => {
  const params = useParams();
  const isNonMobile = useMediaQuery('(min-width:600px)');

  const [formValues, setFormValues] = useState({
    agent_srl: '',
    parents_agent: '',
    upperAgent: 'admin',
    id: '',
    prefix: '',
    password: '',
    confirmPassword: '',
    fee: 0,
    created_at: '',
    vendor_set: '',  // vendor_set 추가
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [parentFee, setParentFee] = useState(0); // 상위 에이전트의 fee 값
  const [currentUser, setCurrentUser] = useState(null); // 현재 로그인한 사용자 정보

  // 현재 로그인한 사용자 정보 가져오기
  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUser(user);
      console.log('로그인 사용자 정보:', user);
    }
  }, []);

  useEffect(() => {
    const fetchAgentInfo = async () => {
      if (!params.id) return;

      setLoading(true);
      try {
        const response = await axios.get(`/agent/info/${params.id}`);
        if (response.data.success && response.data.agent) {
          const agent = response.data.agent;
          setFormValues(prev => ({
            agent_srl: agent.agent_srl || '',
            parents_agent: agent.parents_agent || '',
            upperAgent: agent.parents_agent || '',
            id: agent.id || '',
            prefix: agent.prefix || '',
            fee: agent.fee || 0,
            created_at: agent.created_at || '',
            password: agent.password || '',
            confirmPassword: agent.password || '',
            vendor_set: agent.vendor_set || 'off',  // 기본값은 'off'로 설정
          }));

          // 상위 에이전트 정보 가져오기
          if (agent.parents_agent) {
            try {
              const parentResponse = await axios.get(`/agent/info/${agent.parents_agent}`);
              if (parentResponse.data.success && parentResponse.data.agent) {
                const parentAgent = parentResponse.data.agent;
                setParentFee(parentAgent.fee || 0);
                console.log('상위 에이전트 요율:', parentAgent.fee);
              }
            } catch (parentError) {
              console.error('상위 에이전트 정보 조회 실패:', parentError);
            }
          }
        }
      } catch (error) {
        console.error('에이전트 정보 조회 실패:', error);
        setError('에이전트 정보를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchAgentInfo();
  }, [params.id]);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (field, value) => {
    setError(''); // 에러 메시지 초기화
    setSuccess(''); // 성공 메시지 초기화
    
    // fee 값이 변경될 때 상위 에이전트의 fee 값과 비교
    if (field === 'fee') {
      
      if (value === '') {
        setFormValues(prev => ({ ...prev, [field]: value }));
        setError('요율은 숫자여야 합니다.');
        return;
      }
      
      const newFee = parseFloat(value);
      if (isNaN(newFee)) {
        setError('요율은 숫자여야 합니다.');
        // 여기서 return하지 않고 상태는 업데이트
      } else if (newFee < parseFloat(parentFee)) {
        setError(`하위 에이전트의 요율은 상위 에이전트의 요율(${parentFee})보다 작을 수 없습니다.`);
      } else {
        setError(''); // 유효한 경우 에러 메시지 제거
      }
    }

    // 항상 상태는 업데이트
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  const handleFormSubmit = async () => {
    // 요율 검증 (하위 에이전트의 요율은 상위 에이전트보다 작을 수 없음)
    const feeValue = parseFloat(formValues.fee);
    const parentFeeValue = parseFloat(parentFee);
    
    if (feeValue < parentFeeValue) {
      setError(`하위 에이전트의 요율(${feeValue})은 상위 에이전트의 요율(${parentFeeValue})보다 작을 수 없습니다.`);
      return;
    }

    try {
      // GET 요청은 params 옵션을 사용하여 쿼리 파라미터로 데이터 전송
      const params = {
        agent_id: formValues.id,
        parents_agent: formValues.parents_agent,
        fee: formValues.fee,
        vendor_set: formValues.vendor_set  // vendor_set 추가
      };

      console.log('요청 파라미터:', params);

      const response = await axios.get('/agent/update', { params });

      if (response.data.success) {
        setSuccess('에이전트 수정을 완료했습니다.');
        // 필요하다면 여기서 새로운 정보로 상태 업데이트
      } else {
        setError(response.data.message || '에이전트 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('에이전트 수정 중 오류:', error);
      setError(error.response?.data?.message || '에이전트 수정 중 오류가 발생했습니다.');
    }
  };

  // 관리자 권한 여부 확인
  const isAdmin = currentUser && currentUser.grade === 'admin';

  return (
    <>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            p: 3,
            bgcolor: 'linear-gradient(to bottom, #f8f9fa, #e9ecef)',
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: isNonMobile ? 4 : 3,
              borderRadius: 3,
              maxWidth: 600,
              width: '100%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              background: '#ffffff',
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              gutterBottom
              sx={{
                mb: 4,
                textAlign: 'center',
                color: '#6f42c1',
                textTransform: 'uppercase',
              }}
            >
              에이전트 수정2 - {params.id}
            </Typography>
            
            {/* 에러 메시지 표시 */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {/* 성공 메시지 표시 */}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <Box
              display="flex"
              flexDirection="column"
              gap={3}
              sx={{
                '& .MuiTextField-root': {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#6f42c1',
                    },
                  },
                  '& label': {
                    color: '#6c757d',
                  },
                },
              }}
            >
              {[
                { label: '에이전트 번호', value: formValues.agent_srl, disabled: true },
                { label: '상위 에이전트', value: formValues.upperAgent, disabled: true },
                { label: '소속 에이전트', value: formValues.parents_agent, disabled: true },
                {
                  label: '아이디',
                  value: formValues.id,
                  disabled: true,
                },
                {
                  label: '고유코드',
                  value: formValues.prefix,
                  disabled: true,
                },
                {
                  label: '요율',
                  value: formValues.fee,
                  onChange: e => handleInputChange('fee', e.target.value),
                  disabled: false,
                  helperText: `상위 에이전트 요율: ${parentFee}% (요율은 상위 에이전트보다 작을 수 없습니다)`
                },
              ].map((field, index) => (
                <TextField
                  key={index}
                  label={field.label}
                  fullWidth
                  value={field.value}
                  onChange={field.onChange}
                  size="medium"
                  disabled={field.disabled}
                  helperText={field.helperText}
                  error={field.label === '요율' && parseFloat(formValues.fee) < parseFloat(parentFee)}
                />
              ))}
              
              {/* 벤더 설정 옵션 - admin 등급만 볼 수 있음 */}
              {isAdmin && (
                <FormControl fullWidth sx={{ 
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:hover fieldset': {
                      borderColor: '#6f42c1',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6c757d',
                  },
                }}>
                  <InputLabel id="vendor-set-label">벤더 설정</InputLabel>
                  <Select
                    labelId="vendor-set-label"
                    value={formValues.vendor_set}
                    label="벤더 설정"
                    onChange={e => handleInputChange('vendor_set', e.target.value)}
                  >
                    <MenuItem value="on">활성화</MenuItem>
                    <MenuItem value="off">비활성화</MenuItem>
                  </Select>
                </FormControl>
              )}
              
              {/* 비밀번호 필드 주석 처리 */}
              
              <TextField label="가입 시각" fullWidth value={formValues.created_at} disabled size="medium" />
              <Button
                onClick={handleFormSubmit}
                variant="contained"
                fullWidth
                disabled={parseFloat(formValues.fee) < parseFloat(parentFee)}
                sx={{
                  bgcolor: '#6f42c1',
                  color: 'white',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  fontSize: '16px',
                  py: 1.5,
                  ':hover': {
                    bgcolor: '#563d7c',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(111, 66, 193, 0.5)',
                  }
                }}
              >
                수정
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </>
  );
};

export default AgentUpdate;